/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  AxiosRequestHeaders,
} from "axios";

import type { ModalFunc } from "ant-design-vue/lib/modal/Modal";

import axios from "axios";

import { Modal } from "ant-design-vue";

import { verifyLogout } from "@/utils/register";

import { useRouter } from "@/router";
import { useStore } from "@/store";

let sysModal: ReturnType<ModalFunc>;

/**
 * 出现异常，重新登录弹窗
 */
function confirm(resultDesc: string) {
  if (sysModal) sysModal.destroy();
  sysModal = Modal.error({
    title: "提示",
    content: resultDesc || "系统异常请重新登录或联系管理员",
    okText: "重新登录",
    keyboard: false,
    onOk() {
      const router = useRouter();
      verifyLogout(
        (router?.currentRoute?.value?.name || "") as any,
        router,
        true
      );
      sysModal.destroy();
    },
  });
}

function setToken(token: string) {
  if (localStorage.getItem("isLogin")) {
    localStorage.setItem("token", token);
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 7); // 7天后过期

    const cookieString = `token=${token}; expires=${expirationDate.toUTCString()}; path=/; ${
      location.protocol === "https:" ? "secure;" : ""
    } samesite=strict`;
    document.cookie = cookieString;
  }
}

export function getToken() {
  return localStorage.getItem("token");
}

export function transformUrl(url: string): string {
  const env = process.env;
  if (env.VUE_APP_ENV === "custom") return url;
  if (env.VUE_APP_ENV === "development") {
    url = url.replace("/api", "");
  }
  if (url.indexOf("http") === 0) return url;
  url = `${env.VUE_APP_AXIOS_URL ? env.VUE_APP_AXIOS_URL : ""}${url}`;
  return url;
}

export function mergeHeaders(
  headers?: AxiosRequestHeaders
): AxiosRequestHeaders {
  headers = headers || {};
  !headers["Content-Type"] && (headers["Content-Type"] = "application/json");
  const token = getToken();
  token && (headers["token"] = token);
  return headers as AxiosRequestHeaders;
}

export interface FileBlobResult {
  blob: Blob;
  fileName: string;
}

export interface Result<T = any> {
  page: any;
  resultCode: number;
  resultDesc: string;
  data: T;
  dataList: T;
}

const serve: AxiosInstance = axios.create({
  withCredentials: true,
  timeout: 1000 * 60 * 60 * 1.5,
});

// 请求前拦截
serve.interceptors.request.use(async (config) => {
  // Safety net: ensure cross-origin cookies are included unless explicitly disabled.
  // This avoids cases where some requests end up credential-less unexpectedly.
  if (config.withCredentials === undefined) {
    config.withCredentials = true;
  }
  if (process.env.VUE_APP_ENV !== "custom") {
    config.url = transformUrl(config.url as string);
  }
  config.headers = mergeHeaders(config.headers);

  // Debug (test env): quickly verify which requests are credentialed.
  if (process.env.VUE_APP_ENV === "development") {
    // eslint-disable-next-line no-console
    console.debug("[axios] request", {
      url: config.url,
      method: config.method,
      withCredentials: config.withCredentials,
    });
  }
  return config;
});

// 请求响应拦截
serve.interceptors.response.use(
  (res: AxiosResponse<Result>) => {
    return new Promise((resolve, reject) => {
      const token = res.headers["token"];
      token && setToken(token);

      const { resultCode, resultDesc } = res.data;
      if (resultCode === 100) {
        return resolve(res);
      } else if ([400].includes(resultCode)) {
        const store = useStore();

        store.dispatch("user/logout");
        confirm(resultDesc);
        return reject(res.data);
      }
      return reject(res.data);
    });
  },
  (error) => {
    log.error("error", error);
    const { code, message } = error || {};
    const err: string = error?.toString?.() ?? "";
    let resultDesc = "";
    try {
      if (code === "ECONNABORTED" && message.indexOf("timeout") !== -1) {
        resultDesc = "接口请求超时，请刷新页面重试";
      }
      if (err?.includes("Network Error")) {
        resultDesc = "网络异常请检查你的网络链接是否正常";
      }
    } catch (e) {
      throw new Error(e as unknown as string);
    }
    error.resultDesc = resultDesc;
    return Promise.reject(error);
  }
);

const request = <T = any>(config: AxiosRequestConfig): Promise<T> => {
  return new Promise((resolve, reject) => {
    serve
      .request<any, AxiosResponse<Result>>(config)
      .then((res: AxiosResponse<Result>) => {
        if (res.data.data) {
          if (res.data.page) {
            resolve({
              data: res.data.data,
              page: res.data.page,
            } as unknown as Promise<T>);
          }
          resolve(res.data.data as Promise<T>);
        } else if (res.data.dataList?.length) {
          resolve(res.data.dataList as Promise<T>);
        } else {
          resolve(res.data.data as Promise<T>);
        }
      })
      .catch((e: Error | AxiosError) => {
        reject(e);
      });
  });
};

export default request;
